import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/kit";
import { Bot, Send, User, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How much money did we make this month?",
  "Which sales rep has the highest closing percentage?",
  "Which jobs are below 30% gross margin?",
  "Who owes us money and what bills are due this week?",
  "Which leads have not been contacted or proposals not signed?",
  "How much cash should we have 30 days from now?",
];

export default function AIManager() {
  const [msgs, setMsgs] = useState([
    { role: "ai", text: "I'm your AI Business Manager. Every answer I give is grounded in your live CRM, estimating, production and accounting data — I never make up numbers. Ask me anything." },
  ]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async (text) => {
    const question = (text ?? q).trim();
    if (!question) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setQ(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { question });
      setMsgs((m) => [...m, { role: "ai", text: data.answer, data: data.grounded_data }]);
    } catch { toast.error("AI unavailable"); setMsgs((m) => [...m, { role: "ai", text: "I couldn't reach the model just now. Try again." }]); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader title="AI Business Manager" subtitle="Plain-English answers grounded in your live data — no hallucinated numbers" />
      <div className="mb-card flex flex-col h-[calc(100vh-260px)] min-h-[420px]">
        <div className="flex-1 overflow-y-auto mb-scroll p-4 space-y-4">
          {msgs.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-slate-200 text-slate-700" : "bg-navy text-white"}`}>
                {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </div>
              <div className={`rounded-2xl px-4 py-3 text-sm max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-navy text-white" : "bg-slate-50 border border-slate-200 text-slate-700"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && <div className="flex gap-3"><div className="w-8 h-8 rounded-lg bg-navy text-white flex items-center justify-center"><Bot className="w-4 h-4" /></div><div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-400">Analyzing your data…</div></div>}
          <div ref={endRef} />
        </div>
        {msgs.length <= 1 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid="ai-suggestion" onClick={() => send(s)}
                className="text-xs bg-slate-100 hover:bg-navy hover:text-white transition-colors text-slate-600 px-3 py-1.5 rounded-full flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {s}
              </button>
            ))}
          </div>
        )}
        <div className="border-t border-slate-200 p-3 flex gap-2">
          <input data-testid="ai-chat-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask about revenue, margins, cash, reps, leads…"
            className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-navy" />
          <button data-testid="ai-chat-send" onClick={() => send()} disabled={loading}
            className="bg-brandred text-white px-5 rounded-xl border-2 border-brandred font-bold flex items-center gap-2 disabled:opacity-60">
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}
